'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type HeaderSlotValue = {
  titleCustom?: string;
  rightContent?: ReactNode;
};

const HeaderSlotStateContext = createContext<HeaderSlotValue>({});
const HeaderSlotSetterContext = createContext<
  ((_value: HeaderSlotValue) => void) | null
>(null);

export const HeaderSlotProvider = ({ children }: { children: ReactNode }) => {
  const [slot, setSlot] = useState<HeaderSlotValue>({});

  return (
    <HeaderSlotSetterContext.Provider value={setSlot}>
      <HeaderSlotStateContext.Provider value={slot}>
        {children}
      </HeaderSlotStateContext.Provider>
    </HeaderSlotSetterContext.Provider>
  );
};

export const useHeaderSlotValue = () => useContext(HeaderSlotStateContext);

export const useSetHeaderSlot = ({
  titleCustom,
  rightContent,
}: HeaderSlotValue) => {
  const setSlot = useContext(HeaderSlotSetterContext);

  if (!setSlot) {
    throw new Error('useSetHeaderSlot must be used within HeaderSlotProvider');
  }

  const value = useMemo(
    () => ({ titleCustom, rightContent }),
    [titleCustom, rightContent],
  );

  useEffect(() => {
    setSlot(value);
    return () => setSlot({});
  }, [setSlot, value]);
};
